import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Lock, Search, Wallet } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const box = { background: '#0F1923', border: '1px solid #2D3748' };
const inputCls = 'px-2 py-1.5 rounded-lg text-xs bg-[#1A2433] border border-[#2D3748] text-white w-full';

const PayItemPicker = ({ label, kind, value, onChange, discovered }) => (
  <div className="space-y-1" data-testid={`qb-pay-item-${kind}`}>
    <p className="text-xs font-medium text-white">{label}</p>
    {discovered.length > 0 && (
      <select className={inputCls} value={discovered.some((d) => d.id === value.id) ? value.id : ''} data-testid={`qb-pay-item-${kind}-select`}
        onChange={(e) => { const d = discovered.find((x) => x.id === e.target.value); onChange(d ? { id: d.id, name: d.name } : { id: '', name: '' }); }}>
        <option value="">— pick a pay item found in QuickBooks —</option>
        {discovered.map((d) => <option key={d.id} value={d.id}>{d.name || `id ${d.id}`} (used {d.uses}×)</option>)}
      </select>
    )}
    <div className="flex gap-1.5">
      <input className={inputCls} placeholder="Pay item id" value={value.id} onChange={(e) => onChange({ ...value, id: e.target.value })} data-testid={`qb-pay-item-${kind}-id`} />
      <input className={inputCls} placeholder="Name (optional)" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid={`qb-pay-item-${kind}-name`} />
    </div>
  </div>
);

// Settings → QuickBooks Online: map Regular / Overtime pay items so pushed hours land on the right pay type (Sep 2026).
export const PayItemsMapping = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [reg, setReg] = useState({ id: '', name: '' });
  const [ot, setOt] = useState({ id: '', name: '' });
  const [discovered, setDiscovered] = useState([]);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const apply = (d) => {
    setReg({ id: d.mapping?.regular?.id || '', name: d.mapping?.regular?.name || '' });
    setOt({ id: d.mapping?.overtime?.id || '', name: d.mapping?.overtime?.name || '' });
    setDiscovered(d.discovered || []);
    setInfo(d);
  };
  const scan = async () => {
    setBusy(true); setMsg(null);
    try { apply((await axios.get(`${API}/qb/pay-items`, auth)).data); } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || 'Scan failed.' }); } finally { setBusy(false); }
  };
  useEffect(() => { scan(); }, [token]);
  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await axios.post(`${API}/qb/pay-items`, { regular: reg, overtime: ot }, auth);
      const m = r.data.mapping;
      setMsg({ ok: true, text: m.regular || m.overtime ? `Saved — pushes now tag ${m.regular ? `Regular → ${m.regular.name || m.regular.id}` : 'Regular (no item)'}${m.overtime ? `, Overtime → ${m.overtime.name || m.overtime.id}` : ''}.` : 'Mapping cleared — pushes send plain hours again.' });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || 'Could not save.' }); } finally { setSaving(false); }
  };
  return (
    <div className="mt-4 p-4 rounded-xl" style={box} data-testid="qb-pay-items-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white flex items-center gap-1.5"><Wallet className="w-4 h-4" style={{ color: '#00C896' }} /> Pay items for payroll</h4>
          <p className="text-xs" style={{ color: '#94A3B8' }}>Tag every pushed hour with a QuickBooks pay item so Regular and Overtime land on the right pay type when you run payroll — no manual moves.</p>
        </div>
        <button onClick={scan} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50" style={{ background: '#1E88E5', color: '#fff' }} data-testid="qb-pay-items-scan-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {busy ? 'Scanning…' : 'Find pay items in QuickBooks'}
        </button>
      </div>
      {info && (
        <p className="text-[11px] mt-2" style={{ color: '#94A3B8' }} data-testid="qb-pay-items-scan-info">
          {info.connected ? `${info.discovered.length} pay item(s) found across ${info.scanned} time activities from the last year.` : info.message}
          {info.connected && info.message ? ` ${info.message}` : ''}
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <PayItemPicker label="Regular hours →" kind="regular" value={reg} onChange={setReg} discovered={discovered} />
        <PayItemPicker label="Overtime hours →" kind="overtime" value={ot} onChange={setOt} discovered={discovered} />
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50" style={{ background: '#00C896', color: '#0F1923' }} data-testid="qb-pay-items-save-btn">
          {saving ? 'Saving…' : 'Save pay items'}
        </button>
        {msg && <p className="text-xs" style={{ color: msg.ok ? '#00C896' : '#F59E0B' }} data-testid="qb-pay-items-msg">{msg.text}</p>}
      </div>
      <p className="text-[11px] mt-2" style={{ color: '#64748B' }}>QuickBooks only accepts pay items that exist on the employee&apos;s payroll profile (Payroll → Employees → Pay types). Verify with one worker first: push one day, then check the pay type in QuickBooks → Time.</p>
    </div>
  );
};

// Settings → QuickBooks Online: switch for locking pushed worker-days.
export const AutoLockSwitch = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [on, setOn] = useState(null);
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    axios.get(`${API}/qb/pushed-days`, { params: { start_date: today, end_date: today }, ...auth }).then((r) => setOn(r.data.auto_lock)).catch(() => setOn(true));
  }, [token]);
  const toggle = async () => {
    const next = !on;
    setOn(next);
    try { await axios.put(`${API}/qb/auto-lock`, { enabled: next }, auth); } catch { setOn(!next); }
  };
  if (on === null) return null;
  return (
    <div className="mt-4 p-4 rounded-xl flex items-center justify-between gap-3" style={box} data-testid="qb-auto-lock-section">
      <div>
        <h4 className="text-sm font-semibold text-white flex items-center gap-1.5"><Lock className="w-4 h-4" style={{ color: '#F59E0B' }} /> Lock hours after they reach QuickBooks</h4>
        <p className="text-xs" style={{ color: '#94A3B8' }}>Once a worker-day is pushed, nobody can edit, add to or delete its sessions. Corrections go through Payroll → Push to QuickBooks → Locked days → Unlock.</p>
      </div>
      <button role="switch" aria-checked={on} onClick={toggle} className="shrink-0 relative w-11 h-6 rounded-full transition-colors" style={{ background: on ? '#00C896' : '#2D3748' }} data-testid="qb-auto-lock-switch">
        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: on ? 'translateX(22px)' : 'translateX(2px)' }} />
      </button>
    </div>
  );
};
